/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */

import AutocompletePrediction from '../../../../../types/autocompletePrediction';
import { addressFields, countryNames, keyCodes, provinceCodes, stateCodes, countryCodes } from '../constants';
import { AddressInput } from '../types';
import { supportUSAndCanadaAddressInputs, locale } from '../config';
import {
  compact,
  constant,
  defaults,
  defaultTo,
  each,
  extend,
  find,
  get,
  includes,
  last,
  mapValues,
  trim,
  without,
  zipObject,
} from '@utils/lodash';

interface FormattedAddress {
  firstLine: string;
  secondLine: string;
}

const countryCodeMap: Record<countryCodes.Types.CountryCode, countryNames.Types.CountryName> = {
  ca: 'Canada',
  us: 'United States',
};

export const getNewSelectedIndex = function (
  currentSelectedIndex: number,
  currentListLength: number,
  key: number,
): number {
  const { DOWN_ARROW, UP_ARROW } = keyCodes;

  // If the user pages *past* the bottom element, set the element to be unselected.
  if (key === DOWN_ARROW && currentSelectedIndex === currentListLength - 1) {
    return -1;
  } else if (key === DOWN_ARROW) {
    return currentSelectedIndex + 1;
  } else if (key === UP_ARROW && currentSelectedIndex === -1) {
    return currentListLength - 1;
  } else if (key === UP_ARROW) {
    return currentSelectedIndex - 1;
  }

  return -1;
};

function getFormattedAddressFromString(address: string): FormattedAddress {
  const addressComponents = address.split(', ');

  if (addressComponents.length === 6) {
    return {
      firstLine: addressComponents.slice(0, 2).join(' '),
      secondLine: addressComponents.slice(2).join(' '),
    };
  } else {
    return {
      firstLine: addressComponents[0],
      secondLine: addressComponents.slice(1).join(' '),
    };
  }
}

// Formats address with comma between each segment for split in getFormattedAddressFromString
export function dataModelAddressToString(address?: AddressInput.Address | null): string {
  if (!address) {
    return '';
  }

  const en_US_addressToString: string = compact([
    address.line1,
    address.line2,
    address.city,
    address.stateCode,
    address.postalCode,
    address.countryCode,
  ]).join(', ');

  const en_CA_addressToString: string = compact([
    address.line1,
    address.line2,
    address.city,
    address.provinceCode,
    address.postalCode,
    address.countryCode,
  ]).join(', ');

  if (supportUSAndCanadaAddressInputs && address.countryCode) {
    switch (address.countryCode) {
      case countryNames.UNITED_STATES:
        return en_US_addressToString;
      case countryNames.CANADA:
        return en_CA_addressToString;
      // default case cannot be reached due to surrounding if - ignoring test coverage
      /* istanbul ignore next */
      default:
        return en_US_addressToString;
    }
  }

  switch (locale) {
    case 'en_US':
      return en_US_addressToString;
    case 'en_CA':
      return en_CA_addressToString;
    default:
      return en_US_addressToString;
  }
}

export const formatDMAddresses = (addresses: AddressInput.Address[]) => {
  const formattedDMAddresses = addresses.map(address => {
    const addressDescription = dataModelAddressToString(address);
    return getFormattedAddressFromString(addressDescription);
  });

  return formattedDMAddresses;
};

export const formatAPIAddresses = function (addresses: AutocompletePrediction[]): FormattedAddress[] {
  return addresses.map(address => {
    const formattedAddress = getFormattedAddressFromString(address.description);
    formattedAddress.firstLine = compact([address.context, formattedAddress.firstLine]).join(' ');
    return formattedAddress;
  });
};

function createAddressObject(
  line1?: string,
  line2?: string,
  city?: string,
  state?: string | null,
  zip?: string,
  countyName?: string,
  countryCode?: string,
): AddressInput.Address {
  const en_US_addressObjectFields: Record<addressFields.Types.USAddressField, string | null | undefined> = {
    line1,
    line2,
    city,
    stateCode: state,
    postalCode: zip,
    county: countyName,
    countryCode: 'USA',
  };

  const en_CA_addressObjectFields: Record<addressFields.Types.CanadaAddressField, string | null | undefined> = {
    line1,
    line2,
    city,
    provinceCode: state,
    postalCode: zip,
    countryCode,
  };

  const addressObject = (() => {
    if (supportUSAndCanadaAddressInputs && countryCode) {
      switch (countryCode) {
        case countryNames.UNITED_STATES:
          return en_US_addressObjectFields;
        case countryNames.CANADA:
          return en_CA_addressObjectFields;
        // default case cannot be reached due to surrounding if - ignoring test coverage
        /* istanbul ignore next */
        default:
          return en_US_addressObjectFields;
      }
    }

    switch (locale) {
      case 'en_US':
        return en_US_addressObjectFields;
      case 'en_CA':
        return en_CA_addressObjectFields;
      default:
        return en_US_addressObjectFields;
    }
  })() as AddressInput.Address;

  // Set any undefined values to be empty string so that frontend components will
  // clear fields properly
  return defaults(addressObject, mapValues(addressObject, constant('')));
}

function stringToStateCode(state?: string) {
  if (!state) {
    return '';
  }

  const normalizedState = find(stateCodes.states, function ({ value }) {
    return value === state.toLowerCase() || value === state.toUpperCase();
  });

  if (normalizedState) {
    return normalizedState.value;
  }
  // we've done all we can
  return '';
}

function stringToCountryCode(country?: string) {
  // due to the way the address split is done in parseUSAAddressFromString and parseCanadaAddressFromString
  // country should always be defined so ignoring coverage
  /* istanbul ignore next */
  if (!country) {
    return '';
  }

  const normalizedCountry = find(countryCodes.countries, function (code) {
    return code === country.toLowerCase() || code === country.toUpperCase();
  });

  if (normalizedCountry) {
    return normalizedCountry;
  }

  // we've done all we can
  return '';
}

function getNormalizedProvinceCode(province?: string) {
  if (!province) {
    return '';
  }

  const normalizedProvince = find(provinceCodes.canadaProvinces, function ({ value }) {
    return value === province.toLowerCase() || value === province.toUpperCase();
  });

  if (normalizedProvince) {
    return normalizedProvince.value;
  }
  // we've done all we can
  return '';
}
export const parseAddressFromString = function (
  address = '',
  country?: countryNames.Types.CountryName,
): AddressInput.Address {
  // fallback on locale if country is not passed in since this is how the function previously worked
  if (!country) {
    // const { locale } = globalWrappers.getDeploymentConfig();
    switch (locale) {
      case 'en_US':
        return parseUSAAddressFromString(address);
      case 'en_CA':
        return parseCanadaAddressFromString(address);
      default:
        return parseUSAAddressFromString(address);
    }
  }

  switch (country) {
    case 'United States':
      return parseUSAAddressFromString(address);
    case 'Canada':
      return parseCanadaAddressFromString(address);
    // default case cannot be reached due to previous if - ignoring test coverage
    /* istanbul ignore next */
    default:
      return parseUSAAddressFromString(address);
  }
};
// Note (il): This was taken from a legacy part of the codebase and not refactored because of time pressure.
// Highly recommend creating a massive test suite and refactoring it.
const parseUSAAddressFromString = function (address: string): AddressInput.Address {
  const commaSplit = address.split(',');
  const longestLength = supportUSAndCanadaAddressInputs ? 7 : 6;
  const hasAdditionalLineText = supportUSAndCanadaAddressInputs ? commaSplit.length === 6 : commaSplit.length === 5;
  // handle our formatted case
  if (commaSplit.length === longestLength) {
    const clean = commaSplit.map(str => str.trim());
    // eslint-disable-next-line prefer-spread
    return createAddressObject.apply(null, clean as any);
  }

  const split = address.split(' ');

  // We do this because we want to start from the end and *not* traverse all the way
  // to the beginning. If we start from the beginning, there's a risk of parsing a 5-digit
  // street address as a zip code.
  const possibleZipStrings = [...split].reverse().slice(0, 3);
  let zip = find<any>(possibleZipStrings, function (str) {
    str = str.replace(',', '');
    return str.length === 5 && /^\d+$/.test(str);
  });

  // country should be at the end of the address
  let country =
    supportUSAndCanadaAddressInputs && stringToCountryCode(last(split)) !== ''
      ? stringToCountryCode(last(split))
      : undefined;

  let rawState = find(split, function (str) {
    str = str.replace(',', '');
    return stringToStateCode(str) !== '';
  });

  if (country) {
    country = countryCodeMap[country.replace(',', '').toLowerCase() as countryCodes.Types.CountryCode];
  }
  if (zip) {
    zip = zip.replace(',', '');
  }
  if (rawState) {
    rawState = rawState.replace(',', '');
  }

  const state = stringToStateCode(rawState);
  const street = commaSplit[0];
  const [city, line2] = (function () {
    if (hasAdditionalLineText) {
      return [trim(commaSplit[2]), trim(commaSplit[1])];
    }
    return [trim(commaSplit[1]), ''];
  })();

  each([street, city], str => {
    if (str) {
      str = str
        .replace(zip, '')
        .replace(rawState as string, '')
        .trim();
    }
  });

  return createAddressObject(street, line2, city, state, zip, '', country);
};

const parseCanadaAddressFromString = function (address: string): AddressInput.Address {
  const commaSplit = address.split(',');
  const hasAdditionalLineText = supportUSAndCanadaAddressInputs ? commaSplit.length === 6 : commaSplit.length === 5;

  // parse postal code - just get last section
  let postal = find<any>([...commaSplit].reverse(), function (str) {
    str = str.replace(',', '');
    return supportUSAndCanadaAddressInputs
      ? str.trim().length === 8 || str.trim().length === 7
      : str.trim().length === 7 || str.trim().length === 6;
  });

  const split = address.split(' ');

  let rawProvince = find(split, function (str) {
    str = str.replace(',', '');
    return getNormalizedProvinceCode(str) !== '';
  });

  // country should be at the end of the address
  let country =
    supportUSAndCanadaAddressInputs && stringToCountryCode(last(split)) !== ''
      ? stringToCountryCode(last(split))
      : undefined;

  if (country) {
    country = countryCodeMap[country.replace(',', '').toLowerCase() as countryCodes.Types.CountryCode];
  }

  if (postal) {
    postal = postal.replace(',', '');
  }
  if (rawProvince) {
    rawProvince = rawProvince.replace(',', '');
  }

  const province = getNormalizedProvinceCode(rawProvince);
  const street = commaSplit[0];
  const [city, line2] = (function () {
    if (hasAdditionalLineText) {
      return [trim(commaSplit[2]), trim(commaSplit[1])];
    }
    return [trim(commaSplit[1]), ''];
  })();

  each([street, city], str => {
    if (str) {
      str = str
        .replace(postal, '')
        .replace(rawProvince as string, '')
        .trim();
    }
  });

  return createAddressObject(street, line2, city, province, postal, '', country);
};

// Identifies if a given address belongs to a US territory, triggering differences in parseAPIAddress.
// Supported territories are Guam, Northern Mariana Islands, Puerto Rico, and U.S. Virgin Islands
function isUSTerritory(addressComponents: {}): boolean {
  return includes(stateCodes.USTerritories, get(addressComponents, 'country.short_name'));
}

export const parseAPIAddress = function (
  address: google.maps.places.PlaceResult,
  _supportUSAndCanadaAddressInputs?: boolean,
): AddressInput.Address {
  const addressComponents = zipObject(
    (address.address_components || []).map(component => without(component.types, 'political')[0]),
    (address.address_components || []).map(component => ({
      short_name: component.short_name,
      long_name: component.long_name,
    })),
  );

  // Use long names for everything because that's how we like it but also add administrativeAreaLevel1Code so we can
  // get state names because those are sometimes useful.
  const normalized: any = extend(mapValues(addressComponents, 'long_name'), {
    state_code: get(addressComponents, 'administrative_area_level_1.short_name', ''),
  });

  // Some addresses return with a sub-locality listed as a valid city address instead of the locality.
  // The most prominent example for this is boroughs of New York, which are listed as sublocality_level_1, with
  // nothing listed as the locality. We fill the city field with the lowest-level locality defined.
  const cityFields = [
    'locality',
    'sublocality',
    'sublocality_level_1',
    'sublocality_level_2',
    'sublocality_level_3',
    'sublocality_level_4',
    'sublocality_level_5',
  ];
  const lowestLevelCityField = find(cityFields, field => normalized[field]) as any;
  normalized.city = normalized[lowestLevelCityField];

  // DC doesn't populate off of google auto-complete data, so we give it a little help
  if (!normalized.administrative_area_level_2 && normalized.state_code === 'DC') {
    normalized.administrative_area_level_2 = 'District of Columbia';
  }

  // US Territories (as defined above) return data a little differently from google than states
  // Guam is a further special case because the only county in Guam is Guam, so Google Maps doesn't
  // return a county name, so we set it ourselves
  if (isUSTerritory(addressComponents)) {
    normalized.state_code = get(addressComponents, 'country.short_name');
    normalized.administrative_area_level_2 =
      addressComponents?.country?.short_name === 'GU'
        ? 'Guam'
        : get(addressComponents, 'administrative_area_level_1.short_name');
    normalized.country = countryNames.UNITED_STATES;
  }

  // Premise contains the full street address; otherwise, we try and string together your street number and route
  // You must have a street number defined in this case; otherwise, your address won't be complete.
  const street = (
    normalized.premise
      ? normalized.premise
      : normalized.street_number
      ? compact([normalized.street_number, normalized.route]).join(' ')
      : ''
  ).trim();
  return createAddressObject(
    street,
    '',
    normalized.city,
    normalized.state_code,
    normalized.postal_code,
    normalized.administrative_area_level_2,
    _supportUSAndCanadaAddressInputs ? normalized.country : undefined,
  );
};

interface GetAddressInputTypeParams {
  supportUSAndCanadaAddressInputs?: boolean;
  locale?: AddressInput.Locale;
  countryName?: countryNames.Types.CountryName;
}

export function getAddressInputType(params: GetAddressInputTypeParams): countryNames.Types.CountryName {
  const { countryName } = params;
  if (supportUSAndCanadaAddressInputs && !!countryName) {
    return countryName;
  }

  const localeToCountryNameMap: Record<AddressInput.Locale, countryNames.Types.CountryName> = {
    en_US: countryNames.UNITED_STATES,
    en_CA: countryNames.CANADA,
  };

  return defaultTo(locale && localeToCountryNameMap[locale], countryNames.UNITED_STATES);
}
